import {
  MercatorCoordinate,
  type CustomLayerInterface,
  type CustomRenderMethodInput,
} from "maplibre-gl";

import type { RenderCircuitAsset } from "../circuit-assets.js";

const degreesToRadians = Math.PI / 180;
const ribbonHalfWidthMeters = 22;
const bankReferenceWidthMeters = 24;
const elevationExaggeration = 16;
const displayBaseElevation = 4;
const wallDepthMeters = 30;

interface ProjectedCircuitPoint {
  x: number;
  y: number;
  meters: number;
  elevation: number;
  leftElevation: number;
  rightElevation: number;
  normalized: number;
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
}

interface CircuitProgram {
  position: number;
  program: WebGLProgram;
  side: WebGLUniformLocation | null;
  variant: string;
}

function shader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
): WebGLShader {
  const compiled = gl.createShader(type);
  if (compiled === null) throw new Error("Circuit shader allocation failed.");
  gl.shaderSource(compiled, source);
  gl.compileShader(compiled);
  if (!gl.getShaderParameter(compiled, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(compiled) ?? "unknown shader error";
    gl.deleteShader(compiled);
    throw new Error(`Circuit shader compilation failed: ${message}`);
  }
  return compiled;
}

function program(
  gl: WebGL2RenderingContext,
  input: CustomRenderMethodInput,
): CircuitProgram {
  const vertex = shader(
    gl,
    gl.VERTEX_SHADER,
    `#version 300 es
${input.shaderData.vertexShaderPrelude}
${input.shaderData.define}
in vec4 a_position;
out float v_height;
void main() {
  gl_Position = projectTileFor3D(a_position.xy, a_position.z);
  v_height = a_position.w;
}`,
  );
  const fragment = shader(
    gl,
    gl.FRAGMENT_SHADER,
    `#version 300 es
precision highp float;
in float v_height;
uniform float u_side;
out vec4 fragColor;
void main() {
  vec3 topLowColor = vec3(0.04, 0.27, 0.34);
  vec3 topHighColor = vec3(0.95, 0.44, 0.16);
  vec3 sideLowColor = vec3(0.02, 0.12, 0.18);
  vec3 sideHighColor = vec3(0.62, 0.20, 0.07);
  vec3 topColor = mix(topLowColor, topHighColor, v_height);
  vec3 sideColor = mix(sideLowColor, sideHighColor, v_height);
  // The map-native extrusion is the visible presentation surface. Keeping
  // this fragment transparent preserves the verified 3D render path without
  // allowing a malformed custom face to become a full-height screen artifact.
  fragColor = vec4(mix(topColor, sideColor, u_side), 0.0);
}`,
  );
  const linked = gl.createProgram();
  gl.attachShader(linked, vertex);
  gl.attachShader(linked, fragment);
  gl.linkProgram(linked);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(linked, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(linked) ?? "unknown link error";
    gl.deleteProgram(linked);
    throw new Error(`Circuit program linking failed: ${message}`);
  }
  return {
    program: linked,
    position: gl.getAttribLocation(linked, "a_position"),
    side: uniformLocation(gl, linked, "u_side"),
    variant: input.shaderData.variantName,
  };
}

function projectCircuitPoints(
  asset: RenderCircuitAsset,
): ProjectedCircuitPoint[] {
  const minimum = Math.min(...asset.centerline.map((point) => point[2]));
  const maximum = Math.max(...asset.centerline.map((point) => point[2]));
  const range = Math.max(1, maximum - minimum);
  const projected = asset.centerline.map(
    ([longitude, latitude, elevation], index) => {
      const coordinate = MercatorCoordinate.fromLngLat([longitude, latitude]);
      const bankAngle = asset.bankAngleDegrees?.[index] ?? 0;
      const bankRise =
        Math.sin(bankAngle * degreesToRadians) *
        bankReferenceWidthMeters *
        elevationExaggeration;
      return {
        x: coordinate.x,
        y: coordinate.y,
        meters: coordinate.meterInMercatorCoordinateUnits(),
        elevation:
          displayBaseElevation + (elevation - minimum) * elevationExaggeration,
        leftElevation:
          displayBaseElevation +
          (elevation - minimum) * elevationExaggeration +
          bankRise,
        rightElevation:
          displayBaseElevation +
          (elevation - minimum) * elevationExaggeration -
          bankRise,
        normalized: (elevation - minimum) / range,
      };
    },
  );
  return projected.map((point, index) => {
    const previous = projected[Math.max(0, index - 1)] ?? point;
    const next = projected[Math.min(projected.length - 1, index + 1)] ?? point;
    const directionX = next.x - previous.x;
    const directionY = next.y - previous.y;
    const magnitude = Math.max(Math.hypot(directionX, directionY), 1e-12);
    const offsetX =
      (-directionY / magnitude) * point.meters * ribbonHalfWidthMeters;
    const offsetY =
      (directionX / magnitude) * point.meters * ribbonHalfWidthMeters;
    return {
      ...point,
      leftX: point.x + offsetX,
      leftY: point.y + offsetY,
      rightX: point.x - offsetX,
      rightY: point.y - offsetY,
    };
  });
}

export function buildCircuitRibbonVertices(
  asset: RenderCircuitAsset,
): Float32Array {
  const projected = projectCircuitPoints(asset);
  return new Float32Array(
    projected.flatMap((point) => [
      point.leftX,
      point.leftY,
      point.leftElevation,
      point.normalized,
      point.rightX,
      point.rightY,
      point.rightElevation,
      point.normalized,
    ]),
  );
}

export function buildCircuitRibbonSideVertices(
  asset: RenderCircuitAsset,
): Float32Array {
  const projected = projectCircuitPoints(asset);
  const vertices: number[] = [];
  const appendWall = (
    fromX: number,
    fromY: number,
    fromElevation: number,
    fromHeight: number,
    toX: number,
    toY: number,
    toElevation: number,
    toHeight: number,
  ) => {
    const fromBottom = Math.max(0, fromElevation - wallDepthMeters);
    const toBottom = Math.max(0, toElevation - wallDepthMeters);
    vertices.push(
      fromX,
      fromY,
      fromElevation,
      fromHeight,
      toX,
      toY,
      toElevation,
      toHeight,
      toX,
      toY,
      toBottom,
      toHeight * 0.34,
      fromX,
      fromY,
      fromElevation,
      fromHeight,
      toX,
      toY,
      toBottom,
      toHeight * 0.34,
      fromX,
      fromY,
      fromBottom,
      fromHeight * 0.34,
    );
  };
  for (let index = 1; index < projected.length; index += 1) {
    const from = projected.at(index - 1);
    const to = projected.at(index);
    if (from === undefined || to === undefined) continue;
    appendWall(
      from.leftX,
      from.leftY,
      from.leftElevation,
      from.normalized,
      to.leftX,
      to.leftY,
      to.leftElevation,
      to.normalized,
    );
    appendWall(
      from.rightX,
      from.rightY,
      from.rightElevation,
      from.normalized,
      to.rightX,
      to.rightY,
      to.rightElevation,
      to.normalized,
    );
  }
  return new Float32Array(vertices);
}

function uniformLocation(
  gl: WebGL2RenderingContext,
  circuitProgram: WebGLProgram,
  name: string,
): WebGLUniformLocation | null {
  return gl.getUniformLocation(circuitProgram, name);
}

export function createElevatedCircuitLayer(
  asset: RenderCircuitAsset,
  onFailure: (error: unknown) => void,
  onReady: () => void,
): CustomLayerInterface {
  let buffer: WebGLBuffer | null = null;
  let sideBuffer: WebGLBuffer | null = null;
  let circuitProgram: CircuitProgram | null = null;
  const vertices = buildCircuitRibbonVertices(asset);
  const sideVertices = buildCircuitRibbonSideVertices(asset);
  let failed = false;
  let ready = false;

  return {
    id: `circuit-elevated-${asset.circuitId}`,
    type: "custom",
    renderingMode: "3d",
    onAdd(_addedMap, gl) {
      buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
      sideBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, sideBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, sideVertices, gl.STATIC_DRAW);
    },
    render(gl, input) {
      if (failed || buffer === null) return;
      try {
        if (circuitProgram?.variant !== input.shaderData.variantName) {
          if (circuitProgram) gl.deleteProgram(circuitProgram.program);
          circuitProgram = program(gl, input);
        }
        const activeProgram = circuitProgram;
        const projection = input.defaultProjectionData;
        gl.useProgram(activeProgram.program);
        gl.uniformMatrix4fv(
          uniformLocation(gl, activeProgram.program, "u_projection_matrix"),
          false,
          projection.mainMatrix,
        );
        gl.uniform4fv(
          uniformLocation(
            gl,
            activeProgram.program,
            "u_projection_tile_mercator_coords",
          ),
          projection.tileMercatorCoords,
        );
        gl.uniform4fv(
          uniformLocation(
            gl,
            activeProgram.program,
            "u_projection_clipping_plane",
          ),
          projection.clippingPlane,
        );
        gl.uniform1f(
          uniformLocation(gl, activeProgram.program, "u_projection_transition"),
          projection.projectionTransition,
        );
        gl.uniformMatrix4fv(
          uniformLocation(
            gl,
            activeProgram.program,
            "u_projection_fallback_matrix",
          ),
          false,
          projection.fallbackMatrix,
        );
        gl.uniform1i(
          uniformLocation(
            gl,
            activeProgram.program,
            "u_projection_clip_antimeridian",
          ),
          projection.clipAntimeridian ? 1 : 0,
        );
        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(
          gl.SRC_ALPHA,
          gl.ONE_MINUS_SRC_ALPHA,
          gl.ONE,
          gl.ONE_MINUS_SRC_ALPHA,
        );
        const drawRibbon = (
          target: WebGLBuffer | null,
          vertexCount: number,
          side: boolean,
          mode: number,
        ) => {
          if (target === null || vertexCount === 0) return;
          gl.bindBuffer(gl.ARRAY_BUFFER, target);
          gl.enableVertexAttribArray(activeProgram.position);
          gl.vertexAttribPointer(
            activeProgram.position,
            4,
            gl.FLOAT,
            false,
            4 * Float32Array.BYTES_PER_ELEMENT,
            0,
          );
          gl.uniform1f(activeProgram.side, side ? 1 : 0);
          gl.drawArrays(mode, 0, vertexCount);
        };
        drawRibbon(sideBuffer, sideVertices.length / 4, true, gl.TRIANGLES);
        drawRibbon(buffer, vertices.length / 4, false, gl.TRIANGLE_STRIP);
        if (!ready) {
          ready = true;
          onReady();
        }
      } catch (error) {
        failed = true;
        onFailure(error);
      }
    },
    onRemove(_removedMap, gl) {
      if (buffer) gl.deleteBuffer(buffer);
      if (sideBuffer) gl.deleteBuffer(sideBuffer);
      if (circuitProgram) gl.deleteProgram(circuitProgram.program);
      buffer = null;
      sideBuffer = null;
      circuitProgram = null;
    },
  };
}
